import * as React from 'react'
import { connect } from 'react-redux'

import {
  applicationFieldBlurred,
  applicationFieldChanged,
  applicationFieldRegistered,
  applicationFieldUnregistered,
} from 'common/actions/application'
import { Field, FieldData, FieldDataValue, FieldSelectOption } from 'embed/constants'
import { MultiProductState } from 'embed/reducers'

import { validationDataSelector } from 'embed/selectors'
import { ValidationData } from 'common/services/Validation'
import { Fields } from 'embed/constants/fields'
import { Product } from 'even-ts-static'

import { configFloatingLabelInputsSelector, shouldEnableGeoLocationSelector } from 'embed/selectors/config'
import { ApplicationFieldView } from './index'

export interface OwnProps {
  field: Field
  hidden?: boolean
  onChange?: (value: FieldDataValue) => void
  onEnter?: () => void
  product: Product
  selectOptions?: FieldSelectOption[] // Optional override for the default select options fo a field
  label?: string // Optional override for the default label
  placeholder?: string // Optional override for the default placeholder
  styles?: string
  forceFocus?: boolean
}

export interface Props extends OwnProps {
  dispatchApplicationFieldBlurred: (product: Product, field: Field) => void
  dispatchApplicationFieldChanged: (product: Product, field: Field, value) => void
  dispatchApplicationFieldUnregistered: (product: Product, field: Field) => void
  dispatchApplicationFieldRegistered: (product: Product, field: Field) => void
  enableGeoLocation: boolean
  fieldData: FieldData
  selectedVariantId?: string
  useFloatingLabelInputs: boolean
  validationData: ValidationData
}

const ApplicationFieldContainer: React.FC<Props> = ({
  field,
  fieldData,
  dispatchApplicationFieldRegistered,
  dispatchApplicationFieldChanged,
  dispatchApplicationFieldBlurred,
  enableGeoLocation,
  product,
  label,
  placeholder,
  hidden,
  onEnter,
  ...props
}) => {
  React.useEffect(() => {
    if (label !== undefined) {
      field.options.label = label
    }
    if (placeholder !== undefined) {
      field.options.placeholder = placeholder
    }
    dispatchApplicationFieldRegistered(product, field)
    if (field.options.initialValue !== undefined && fieldData?.value === undefined) {
      dispatchApplicationFieldChanged(product, field, field.options.initialValue)
    }
  }, [])

  const onAddressSelect = React.useCallback(
    (address: string, city: string, state: string, zip: string) => {
      switch (field) {
        case Fields.CoApplicantAddressOne:
          dispatchApplicationFieldChanged(product, Fields.CoApplicantAddressOne, address)
          dispatchApplicationFieldBlurred(product, Fields.CoApplicantAddressOne)
          if (enableGeoLocation) {
            dispatchApplicationFieldChanged(product, Fields.CoApplicantCity, city)
            dispatchApplicationFieldBlurred(product, Fields.CoApplicantCity)
            dispatchApplicationFieldChanged(product, Fields.CoApplicantState, state)
            dispatchApplicationFieldBlurred(product, Fields.CoApplicantState)
            dispatchApplicationFieldChanged(product, Fields.CoApplicantZipcode, zip)
            dispatchApplicationFieldBlurred(product, Fields.CoApplicantZipcode)
          }
          break
        case Fields.AddressOne:
        default:
          dispatchApplicationFieldChanged(product, Fields.AddressOne, address)
          if (enableGeoLocation) {
            dispatchApplicationFieldChanged(product, Fields.City, city)
            dispatchApplicationFieldChanged(product, Fields.State, state)
            dispatchApplicationFieldChanged(product, Fields.Zipcode, zip)
          }
      }
    },
    [field, product, enableGeoLocation, dispatchApplicationFieldChanged, dispatchApplicationFieldBlurred],
  )

  const onBlur = React.useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      dispatchApplicationFieldBlurred(product, field)
    },
    [dispatchApplicationFieldBlurred, product, field],
  )

  const onChange = React.useCallback(
    (value: FieldDataValue) => {
      dispatchApplicationFieldChanged(product, field, value)
    },
    [dispatchApplicationFieldChanged, product, field],
  )

  const onKeyDown = React.useCallback(
    (key: string) => {
      // If the field has an onEnter callback and the enter key is pressed, blur the field to show
      // the validation status and call the onEnter callback if the field is valid
      if (onEnter !== undefined && key === 'Enter') {
        dispatchApplicationFieldBlurred(product, field)

        if (fieldData.isValid ?? false) {
          onEnter()
        }
      }
    },
    [dispatchApplicationFieldBlurred, field, fieldData, onEnter, product],
  )

  const onUniversitySelect = React.useCallback(
    (name: string) => {
      dispatchApplicationFieldChanged(product, Fields.University, name)
    },
    [dispatchApplicationFieldChanged, product],
  )

  return !hidden ? (
    <ApplicationFieldView
      onBlur={onBlur}
      onChange={props.onChange || onChange}
      onAddressSelect={onAddressSelect}
      onKeyDown={onKeyDown}
      onUniversitySelect={onUniversitySelect}
      field={field}
      fieldData={fieldData}
      {...props}
    />
  ) : null
}

const mapStateToProps = (state: MultiProductState, ownProps: OwnProps) => ({
  enableGeoLocation: shouldEnableGeoLocationSelector(state),
  fieldData: state.application[ownProps.product]?.fields?.[ownProps.field.key],
  useFloatingLabelInputs: configFloatingLabelInputsSelector(state),
  validationData: validationDataSelector(state),
})

const mapDispatchToProps = (dispatch) => ({
  dispatchApplicationFieldBlurred: (product: Product, field: Field) =>
    dispatch(applicationFieldBlurred(product, field.key)),
  dispatchApplicationFieldChanged: (product: Product, field: Field, value) =>
    dispatch(applicationFieldChanged(product, field.key, value)),
  dispatchApplicationFieldRegistered: (product: Product, field: Field) =>
    dispatch(applicationFieldRegistered(product, field.key)),
  dispatchApplicationFieldUnregistered: (product: Product, field: Field) =>
    dispatch(applicationFieldUnregistered(product, field.key)),
})

export const ApplicationField = connect(mapStateToProps, mapDispatchToProps)(ApplicationFieldContainer)
